// /var/www/ohcampus_frontend_source/src/components/LeadFeesShareDialog.jsx
// ─────────────────────────────────────────────────────────────────────────
//   Dialog used from the Leads table: pick a college + course attached to
//   the lead, choose which phone (primary / alternate) to send to, preview
//   the formatted fee message, then open the wa.me URL in a new tab.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2, Send, Copy, MessageCircle, Phone, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { leadsAPI } from '../lib/api';

const safeArr = (x) => (Array.isArray(x) ? x : []);

export default function LeadFeesShareDialog({ lead, open, onOpenChange }) {
  // College + course picklist derived from the lead's saved selections
  const collegeOptions = useMemo(() => {
    const ids = safeArr(lead?.suggested_college_ids);
    const names = safeArr(lead?.suggested_college_names);
    return ids.map((id, i) => ({ id, name: names[i] || id }));
  }, [lead]);

  const courseOptions = useMemo(() => {
    const ids = safeArr(lead?.course_ids);
    const names = safeArr(lead?.course_names);
    return ids.map((id, i) => ({ id, name: names[i] || id }));
  }, [lead]);

  const phonesAvail = useMemo(() => {
    const out = [];
    if (lead?.phone)     out.push({ value: 'primary', label: `Primary: ${lead.phone}` });
    if (lead?.alt_phone) out.push({ value: 'alt',     label: `Alternate: ${lead.alt_phone}` });
    return out;
  }, [lead]);

  const [collegeId,   setCollegeId]   = useState('');
  const [courseId,    setCourseId]    = useState('');
  const [phoneChoice, setPhoneChoice] = useState('');
  const [includeBrochure, setIncludeBrochure] = useState(true);
  const [preview,     setPreview]     = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [errorMsg,    setErrorMsg]    = useState('');

  // Reset when reopened
  useEffect(() => {
    if (!open) return;
    setErrorMsg('');
    setPreview(null);
    if (collegeOptions.length === 1) setCollegeId(collegeOptions[0].id);
    else setCollegeId('');
    if (courseOptions.length === 1) setCourseId(courseOptions[0].id);
    else setCourseId('');
    setPhoneChoice(phonesAvail[0]?.value || '');
  }, [open, collegeOptions, courseOptions, phonesAvail]);

  const fetchPreview = async () => {
    if (!lead?.id || !collegeId || !courseId || !phoneChoice) {
      setErrorMsg('Pick college, course and a phone number');
      return;
    }
    setLoading(true);
    setErrorMsg('');
    try {
      const r = await leadsAPI.whatsappFees(lead.id, {
        college_id:       collegeId,
        course_id:        courseId,
        phone_choice:     phoneChoice,
        include_brochure: includeBrochure,
      });
      setPreview(r.data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to build message';
      setErrorMsg(msg);
      setPreview(null);
    } finally { setLoading(false); }
  };

  // Auto-fetch preview when selections become valid
  // Clear the preview immediately when selections change so the user
  // never sees stale data for a different college/course while waiting.
  useEffect(() => {
    setPreview(null);
    setErrorMsg('');
  }, [collegeId, courseId, phoneChoice, includeBrochure]);

  useEffect(() => {
    if (open && collegeId && courseId && phoneChoice) {
      fetchPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, collegeId, courseId, phoneChoice, includeBrochure]);

  const openWA = () => {
    if (!preview?.whatsapp_url) return;
    window.open(preview.whatsapp_url, '_blank', 'noopener,noreferrer');
    toast.success('Opening WhatsApp…');
  };

  const copyMsg = async () => {
    if (!preview?.message) return;
    try {
      await navigator.clipboard.writeText(preview.message);
      toast.success('Message copied to clipboard');
    } catch (_e) {
      toast.error('Could not copy — long-press the preview to copy manually');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-wa-fees-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-emerald-600" />
            Send Fees via WhatsApp
            <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-700">
              {lead?.name || 'Lead'}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Pick a college + course attached to this lead. We will build a clean fee message and open WhatsApp with it pre-filled.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">College</Label>
            {collegeOptions.length === 0 ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                No colleges attached to this lead. Edit the lead to add at least one college.
              </p>
            ) : (
              <Select value={collegeId} onValueChange={setCollegeId}>
                <SelectTrigger data-testid="wa-fees-college"><SelectValue placeholder="Choose college" /></SelectTrigger>
                <SelectContent>
                  {collegeOptions.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-xs">Course</Label>
            {courseOptions.length === 0 ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                No courses attached to this lead. Edit the lead to add at least one course.
              </p>
            ) : (
              <Select value={courseId} onValueChange={setCourseId}>
                <SelectTrigger data-testid="wa-fees-course"><SelectValue placeholder="Choose course" /></SelectTrigger>
                <SelectContent>
                  {courseOptions.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div>
          <Label className="text-xs">Send to</Label>
          {phonesAvail.length === 0 ? (
            <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 mt-1">
              This lead has no phone or alternate number. Add one to proceed.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 mt-1">
              {phonesAvail.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPhoneChoice(p.value)}
                  data-testid={`wa-fees-phone-${p.value}`}
                  className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition ${
                    phoneChoice === p.value
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400'
                  }`}
                >
                  <Phone className="h-3 w-3" /> {p.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Brochure toggle */}
        <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="flex items-start gap-2">
            <span className="text-base leading-none mt-0.5">📄</span>
            <div>
              <p className="text-xs font-semibold text-slate-700">Attach College Brochure</p>
              <p className="text-[11px] text-slate-500 leading-tight">
                {preview?.brochure_url
                  ? <>Found: <span className="font-mono break-all">{preview.brochure_title || 'brochure.pdf'}</span></>
                  : (includeBrochure
                      ? <>We will look up a brochure for the selected college.</>
                      : <>Brochure link will not be added to the message.</>)}
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0" data-testid="wa-fees-brochure-toggle">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={includeBrochure}
              onChange={(e) => setIncludeBrochure(e.target.checked)}
            />
            <div className="w-9 h-5 bg-slate-300 rounded-full peer peer-checked:bg-emerald-600 peer-focus:outline-none transition-colors"></div>
            <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-4 shadow"></div>
          </label>
        </div>

        {/* Preview */}
        <div className="border border-slate-100 rounded-lg overflow-hidden">
          <div className="bg-emerald-50 border-b border-emerald-100 px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp Preview
            </span>
            <Button size="sm" variant="ghost" onClick={fetchPreview} disabled={loading} data-testid="wa-fees-refresh" className="text-emerald-700 hover:bg-emerald-100">
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            </Button>
          </div>
          <div
            data-testid="wa-fees-preview"
            className="text-xs font-sans p-3 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%3E%3Crect%20width%3D%2220%22%20height%3D%2220%22%20fill%3D%22%23ECE5DD%22%2F%3E%3C%2Fsvg%3E')] min-h-[180px]"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {loading ? (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Building message…
              </div>
            ) : errorMsg ? (
              <span className="text-rose-700">{errorMsg}</span>
            ) : preview?.message ? (
              <div className="bg-white rounded-lg shadow-sm px-3 py-2 leading-relaxed text-slate-800">
                {preview.message.split('\n').map((line, idx) => {
                  // tiny markdown-ish: *bold*, _italic_
                  let html = line
                    .replace(/\*(.+?)\*/g, '<strong>$1</strong>')
                    .replace(/_(.+?)_/g, '<em class="text-slate-500">$1</em>');
                  return <div key={idx} dangerouslySetInnerHTML={{ __html: html || '&nbsp;' }} />;
                })}
              </div>
            ) : (
              <span className="text-slate-400 italic">Pick selections above to build the message.</span>
            )}
          </div>
          {preview?.phone && (
            <div className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] text-slate-600 flex items-center gap-2 flex-wrap">
              <span>Sending to:</span>
              <span className="font-mono font-semibold text-slate-900">{preview.phone}</span>
              <span className="text-slate-400">·</span>
              <span>{preview.college?.name}</span>
              {preview.course?.name && (
                <>
                  <span className="text-slate-400">·</span>
                  <span>{preview.course.name}</span>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={copyMsg} disabled={!preview?.message} data-testid="wa-fees-copy">
            <Copy className="h-4 w-4 mr-1" /> Copy
          </Button>
          <Button onClick={openWA} disabled={!preview?.whatsapp_url}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="wa-fees-send">
            <Send className="h-4 w-4 mr-1" /> Open in WhatsApp <ExternalLink className="h-3 w-3 ml-1 opacity-70" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
